import { useCallback, useMemo } from 'react';
import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';

interface UsePaginatedListProps<
  TItem,
  TParams extends { offset?: number; limit?: number },
> {
  fetchPage: (params: TParams, signal?: AbortSignal) => Promise<TItem[]>;
  params: TParams;
  queryKey: readonly unknown[];
  enabled?: boolean;
}

export const usePaginatedList = <
  TItem,
  TParams extends { offset?: number; limit?: number },
>({
  fetchPage,
  params,
  queryKey,
  enabled = true,
}: UsePaginatedListProps<TItem, TParams>) => {
  const limit = params.limit ?? 100;

  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } =
    useInfiniteQuery({
      queryKey,
      queryFn: ({ pageParam, signal }) =>
        fetchPage({ ...params, offset: pageParam, limit }, signal),
      initialPageParam: 0,
      getNextPageParam: (lastPage, _allPages, lastPageParam) =>
        lastPage.length < limit ? undefined : lastPageParam + limit,
      placeholderData: enabled ? keepPreviousData : undefined,
      enabled,
    });

  const items = useMemo(() => data?.pages.flat() ?? [], [data]);

  const fetchNextPageIfNeeded = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return {
    items,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    fetchNextPageIfNeeded,
  };
};
